import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { opportunityRepo } from "@repositories";
import { linko } from "@shared/linko";
import { formatOpportunitySchedule } from "@shared/helpers/format-opportunity-schedule";
import { allowsGroupVolunteer } from "@shared/helpers/opportunity-volunteer-mode";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { OpportunityPreviewDialog } from "../../../components/opportunity-preview/OpportunityPreviewDialog";
import { useAddUserToOpportunity } from "../../../queries/events.query";

type RankedOpportunityRow = {
  id: string;
  title: string;
  location: string;
  scheduleLabel: string;
  distanceKm: number | null;
  finalScore: number | null;
  isGroupParent: boolean;
  callCenterUrgency: number;
  callCenterDifficulty: number;
};

function formatDistance(km: number | null | undefined): string {
  if (km == null) return "-";
  if (km < 1) return `${Math.round(km * 1000)} מ'`;
  return `${km.toFixed(1)} ק"מ`;
}

/** Finite lat/lng that is not the (0, 0) placeholder. */
export function hasUsableVolunteerCoordinates(
  coordinates: { lat?: unknown; lng?: unknown } | null | undefined,
): boolean {
  if (!coordinates) return false;
  const { lat, lng } = coordinates;
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0)
  );
}

/** Empty, 0, and negative values mean no distance cap. */
function radiusKmForRequest(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return value;
}

async function fetchRankedOpportunitiesForUser(params: {
  userId: string;
  organizationId: string;
  excludeOpportunityId?: string;
  radiusKm?: number;
}): Promise<RankedOpportunityRow[]> {
  const radiusKm =
    params.radiusKm != null &&
    Number.isFinite(params.radiusKm) &&
    params.radiusKm > 0
      ? params.radiusKm
      : undefined;

  const { data: ranked } =
    await linko.opportunity.queries.getRankedOpportunitiesForUser({
      userId: params.userId,
      organizationId: params.organizationId,
      excludeOpportunityId: params.excludeOpportunityId,
      limit: 50,
      ...(radiusKm != null ? { radiusKm } : {}),
    });

  const rankedList = (ranked ?? []) as Array<{
    id: string;
    distanceFromLocationKM?: number | null;
    finalScore?: number | null;
  }>;

  if (rankedList.length === 0) return [];

  const opportunityIds = rankedList.map((row) => row.id);
  const opportunities = await opportunityRepo.find({
    where: { id: { $in: opportunityIds } },
    select: {
      id: true,
      title: true,
      location: true,
      startDate: true,
      endDate: true,
      hasTime: true,
      hasStartTime: true,
      timeDescription: true,
      volunteerMode: true,
      isOpportunityGroupParent: true,
      parentOpportunityId: true,
      callCenterUrgency: true,
      callCenterDifficulty: true,
    },
  });

  const opportunityById = new Map(opportunities.map((o) => [o.id, o]));
  const scoreById = new Map(
    rankedList.map((row) => [
      row.id,
      {
        distanceKm: row.distanceFromLocationKM ?? null,
        finalScore: row.finalScore ?? null,
      },
    ]),
  );

  const rows: RankedOpportunityRow[] = [];
  for (const id of opportunityIds) {
    const opportunity = opportunityById.get(id);
    if (!opportunity) continue;
    const scores = scoreById.get(id);
    rows.push({
      id: opportunity.id,
      title: opportunity.title ?? "",
      location: opportunity.location ?? "",
      scheduleLabel: formatOpportunitySchedule(opportunity),
      distanceKm: scores?.distanceKm ?? null,
      finalScore: scores?.finalScore ?? null,
      isGroupParent:
        !!opportunity.isOpportunityGroupParent ||
        (allowsGroupVolunteer(opportunity.volunteerMode) &&
          !opportunity.parentOpportunityId),
      callCenterUrgency: opportunity.callCenterUrgency ?? 0,
      callCenterDifficulty: opportunity.callCenterDifficulty ?? 0,
    });
  }

  return rows;
}

export type RelevantOpportunitiesForVolunteerListProps = {
  userId: string;
  userName: string;
  associationId: string;
  organizationId: string;
  excludeOpportunityId?: string;
  /** When false, the ranked query does not run. Defaults to true. */
  enabled?: boolean;
  /**
   * When false, distance filtering is unavailable.
   * When omitted, the radius field stays enabled.
   */
  hasVolunteerCoordinates?: boolean;
};

export function RelevantOpportunitiesForVolunteerList({
  userId,
  userName,
  associationId,
  organizationId,
  excludeOpportunityId,
  enabled = true,
  hasVolunteerCoordinates,
}: RelevantOpportunitiesForVolunteerListProps) {
  const canFetch = !!userId && !!organizationId;
  const coordinatesMissing = hasVolunteerCoordinates === false;
  const { mutateAsync: addUserToOpportunity, isPending: isAdding } =
    useAddUserToOpportunity();
  const [addingOpportunityId, setAddingOpportunityId] = useState<string | null>(
    null,
  );
  const [previewOpportunityId, setPreviewOpportunityId] = useState<
    string | null
  >(null);
  const [previewView, setPreviewView] = useState<"details" | "group-slots">(
    "details",
  );
  const [radiusInput, setRadiusInput] = useState("");
  const [debouncedRadiusInput, setDebouncedRadiusInput] = useState("");
  const [radiusUserId, setRadiusUserId] = useState(userId);
  if (radiusUserId !== userId) {
    setRadiusUserId(userId);
    setRadiusInput("");
    setDebouncedRadiusInput("");
  }
  const radiusKm = coordinatesMissing
    ? undefined
    : radiusKmForRequest(debouncedRadiusInput);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedRadiusInput(radiusInput), 400);
    return () => clearTimeout(timer);
  }, [radiusInput]);

  const queryKey = useMemo(
    () =>
      [
        "ranked-opportunities-for-user",
        userId,
        organizationId,
        excludeOpportunityId,
        radiusKm ?? null,
      ] as const,
    [userId, organizationId, excludeOpportunityId, radiusKm],
  );

  const { data: rows = [], isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchRankedOpportunitiesForUser({
        userId,
        organizationId,
        excludeOpportunityId,
        radiusKm,
      }),
    enabled: enabled && canFetch,
    placeholderData: (previous) => previous,
  });

  const handleAddToOpportunity = async (
    targetOpportunityId: string,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (isAdding) return;

    setAddingOpportunityId(targetOpportunityId);
    try {
      await addUserToOpportunity({
        opportunityId: targetOpportunityId,
        associationId,
        showSuccessAlert: true,
      });
    } catch {
      // cancel or failed add - mutate onError already alerts failures
    } finally {
      setAddingOpportunityId(null);
    }
  };

  return (
    <Box>
      <TextField
        type="number"
        size="small"
        label="מרחק בקילומטרים"
        value={radiusInput}
        disabled={coordinatesMissing}
        onChange={(event) => setRadiusInput(event.target.value)}
        helperText={
          coordinatesMissing
            ? "למתנדב אין כתובת, אי אפשר לסנן לפי מרחק."
            : "לפי הכתובת של המתנדב. שדה ריק מציג הכל. משימות מרחוק נכללות גם עם סינון."
        }
        inputProps={{ min: 0 }}
        sx={{ mb: 2, maxWidth: "22rem", width: "100%" }}
      />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, fontSize: "0.8125rem" }}
      >
        מוצגות עד 50 משימות פתוחות מהארגון לפי ציון התאמה.
        לא כלולות משימות שהמתנדב כבר נרשם אליהן
        {excludeOpportunityId ? " ולא המשימה הנוכחית" : ""}.
      </Typography>

      {!canFetch ? (
        <Typography variant="body2" color="text.secondary">
          חסרים פרטי מתנדב או ארגון להצגת משימות רלוונטיות.
        </Typography>
      ) : isLoading ? (
        <Stack alignItems="center" justifyContent="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 && !isFetching ? (
        <Stack py={2}>לא נמצאו משימות מתאימות.</Stack>
      ) : (
        <>
          {isFetching && !isLoading && (
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                טוען משימות…
              </Typography>
            </Stack>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              stickyHeader
              aria-label={
                userName
                  ? `משימות רלוונטיות עבור ${userName}`
                  : "משימות רלוונטיות"
              }
              sx={{
                "& .MuiTableRow-root[data-clickable='true']:hover": {
                  backgroundColor: "action.hover",
                  cursor: "pointer",
                },
                "& .MuiTableCell-root": { py: 1.25 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>שם משימה</TableCell>
                  <TableCell sx={{ width: 72 }}>דחיפות</TableCell>
                  <TableCell sx={{ width: 72 }}>קושי</TableCell>
                  <TableCell sx={{ width: 130 }}>מועד</TableCell>
                  <TableCell>מיקום</TableCell>
                  <TableCell sx={{ width: 72 }}>מרחק</TableCell>
                  <TableCell sx={{ width: 72 }}>ציון</TableCell>
                  <TableCell sx={{ width: 120 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-clickable="true"
                    onClick={() => {
                      setPreviewView(
                        row.isGroupParent ? "group-slots" : "details",
                      );
                      setPreviewOpportunityId(row.id);
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {row.title || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.callCenterUrgency}</TableCell>
                    <TableCell>{row.callCenterDifficulty}</TableCell>
                    <TableCell>{row.scheduleLabel}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap
                        title={row.location || undefined}
                        sx={{ maxWidth: 180 }}
                      >
                        {row.location || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDistance(row.distanceKm)}</TableCell>
                    <TableCell>
                      {row.finalScore != null
                        ? row.finalScore.toFixed(2)
                        : "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          addingOpportunityId === row.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <PersonAddAlt1Icon />
                          )
                        }
                        disabled={isAdding}
                        onClick={(e) => handleAddToOpportunity(row.id, e)}
                        sx={{ textTransform: "none", borderRadius: "0.75rem" }}
                      >
                        הוספה
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
      <OpportunityPreviewDialog
        open={!!previewOpportunityId}
        opportunityId={previewOpportunityId}
        view={previewView}
        onClose={() => {
          setPreviewOpportunityId(null);
          setPreviewView("details");
        }}
        nestedInDialog
      />
    </Box>
  );
}
